"use client";

import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SafetyFaqFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const trustSeals = [{
    label: "TRAUMA-INFORMED"
  }, {
    label: "NEUTRAL"
  }, {
    label: "COURT-CENTERED"
  }];
  const faqs = [{
    number: "Q.01",
    question: "How do you ensure the safety of children during visits?",
    answer: "Safety is our primary directive. We maintain a strictly controlled, neutral environment where every interaction is monitored by trained professionals. We utilize established safety protocols, including secure entry and exit procedures, to ensure that children remain protected from conflict and stress throughout their time with us.",
    callout: ""
  }, {
    number: "Q.02",
    question: "Are your services neutral and unbiased?",
    answer: "Yes. Tiny to Teen Supervised Parenting & Education Services operates as a strictly neutral third party. Our role is to provide objective, factual observation and support without taking sides. We adhere to professional standards that prioritize the child's well-being above all else, ensuring that our reporting remains impartial for court purposes.",
    callout: "Our staff are trained to maintain professional boundaries and remain detached from parental disputes."
  }, {
    number: "Q.03",
    question: "What is your approach to high-conflict situations?",
    answer: "We are experienced in managing high-conflict dynamics. Our staff are trained in de-escalation techniques and trauma-informed care. We enforce firm boundaries to prevent conflict from impacting the child, and we are prepared to terminate a session immediately if safety or professional standards are compromised.",
    callout: ""
  }, {
    number: "Q.04",
    question: "How is information handled and reported?",
    answer: "All observations are documented in objective, factual reports. These reports are prepared for the court or relevant legal professionals and focus on the child's experience and the quality of the interaction. We maintain strict confidentiality regarding all case files, sharing information only as authorized by court order or legal mandate.",
    callout: "Reports are generated with clinical precision to assist the court in making informed decisions."
  }, {
    number: "Q.05",
    question: "Who is permitted to attend the supervised sessions?",
    answer: "Only authorized individuals specified in the court order or intake agreement are permitted to attend. We verify the identity of all participants before any session begins. No unauthorized visitors are allowed, and we maintain a secure, private facility to ensure the integrity of the visitation process.",
    callout: ""
  }, {
    number: "Q.06",
    question: "Do you provide transportation services?",
    answer: "Yes, we offer secure transportation for children and vulnerable individuals. Our transport services are conducted by trained staff who prioritize safety and comfort, ensuring that the child is moved between locations in a structured, calm, and professional manner.",
    callout: ""
  }, {
    number: "Q.07",
    question: "What happens if a parent violates the rules?",
    answer: "We maintain firm boundaries. Any violation of the established rules or court-ordered conditions results in immediate intervention. Depending on the severity, this may include a verbal warning, the immediate termination of the visit, or a formal report to the court and legal counsel regarding the incident.",
    callout: "Our priority is the child's safety; we do not tolerate behavior that threatens that security."
  }];
  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section data-section-id="2902" id="safety-faq" className="bg-[#E7D8C7] text-[#2F3631] py-20 md:py-32 px-6 md:px-12 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-4 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24">
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#5E6A52] mb-8">
                § Safety & Standards FAQ
              </p>

              <h2 className="font-serif text-[#2F3631] text-4xl md:text-5xl leading-[1.05] tracking-tight mb-8">
                Safety <span className="block">/ is our <em className="italic text-[#8FA27C]">foundation</em>.</span>
              </h2>

              <p className="font-default text-base leading-relaxed text-[#2F3631]/80 mb-10 max-w-md">
                We provide a calm, structured, and neutral environment for families navigating complex transitions. Our commitment to safety and professional boundaries ensures that every child feels secure.
              </p>

              <div className="flex flex-wrap gap-3">
                {trustSeals.map((seal, i) => <div key={i} data-index={i} className="border border-[#5E6A52]/60 px-3 py-1.5 rounded-lg">
                    <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#5E6A52]">
                      {seal.label}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <ul className="border-b border-[#5E6A52]/30">
              {faqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return <li key={i} data-index={i} className="border-t border-[#5E6A52]/30">
                    <button type="button" onClick={() => handleToggle(i)} aria-expanded={isOpen} className="w-full py-8 flex items-start gap-6 text-left group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8FA27C]/40 rounded-lg">
                      <span className="font-mono text-xs tracking-[0.15em] uppercase text-[#5E6A52] pt-1 shrink-0 w-12">
                        {faq.number}
                      </span>

                      <span className="font-default text-[20px] md:text-[22px] leading-snug text-[#2F3631] flex-1">
                        {faq.question}
                      </span>

                      <span className="shrink-0 pt-1">
                        {isOpen ? <Minus className="w-5 h-5 text-[#8FA27C] transition-transform duration-300" strokeWidth={1.25} /> : <Plus className="w-5 h-5 text-[#8FA27C] transition-transform duration-300 group-hover:rotate-90" strokeWidth={1.25} />}
                      </span>
                    </button>

                    <div className="overflow-hidden transition-all ease-out" style={{
                  maxHeight: isOpen ? "1000px" : "0px",
                  transitionDuration: "350ms",
                  opacity: isOpen ? 1 : 0
                }}>
                      <div className="pl-0 sm:pl-[72px] pr-0 sm:pr-12 pb-8">
                        <p className="font-default text-base text-[#2F3631]/85" style={{
                      lineHeight: 1.7
                    }}>
                          {faq.answer}
                        </p>

                        {faq.callout && <div className="mt-6 border border-[#C89F7B]/50 px-5 py-4 rounded-lg">
                            <p className="font-mono text-sm text-[#C89F7B]" style={{
                        lineHeight: 1.6
                      }}>
                              {faq.callout}
                            </p>
                          </div>}
                      </div>
                    </div>
                  </li>;
            })}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}